<template>
  <div class="lottery-cotacao">
    <p class="hint">
      Cadastre a cotação (multiplicador) por quantidade de dezenas (n) e acertos mínimos (MinHits).
      Só as quantidades cadastradas aparecem no app. Prêmio = cotação × valor apostado.
      Lotinha/Quininha/Seninha: tudo ou nada (em geral MinHits = k).
      Binguinho: pool 00–99, quantidade 3 ou 15, faixas parciais, sem descarga.
      Bingo Semanal: pool 00–99, quantidade 7, faixas 7/6/5 acertos, sem descarga.
      Loto Quina: pool 00–99, quantidade 20, faixas 5/4/3 acertos (3000x/100x/10x), sem descarga.
      Aposta mín/máx configurável por modalidade (0 = sem limite).
    </p>

    <div v-if="loading" class="configs-loading">
      <i class="fa-solid fa-spinner fa-spin"></i>
      <span>Carregando cotações...</span>
    </div>

    <div v-else class="lotteries-accordion">
      <div
        v-for="cfg in configs"
        :key="cfg.id || cfg.modalityId"
        class="accordion-item"
      >
        <div
          class="accordion-header"
          :class="{ active: isExpanded(cfg) }"
          @click="toggleAccordion(cfg)"
        >
          <span class="accordion-icon">
            {{ isExpanded(cfg) ? '▼' : '►' }}
          </span>
          <span class="lottery-name">{{ cfg.modalityName || 'Modalidade' }}</span>
          <span class="lottery-count">
            ({{ visibleCotacoes(cfg).length }}
            {{ visibleCotacoes(cfg).length === 1 ? 'cotação' : 'cotações' }})
          </span>
          <label class="active-toggle" @click.stop>
            <input
              type="checkbox"
              v-model="cfg.isActive"
              :disabled="isReadOnly"
              @change="markConfigDirty(cfg)"
            />
            Ativa
          </label>
        </div>

        <div v-show="isExpanded(cfg)" class="accordion-content">
          <div class="config-row">
            <label class="field-label">
              Ordem
              <input
                type="number"
                min="1"
                class="field-input"
                v-model.number="cfg.sortOrder"
                :disabled="isReadOnly"
                :readonly="isReadOnly"
                @input="markConfigDirty(cfg)"
              />
            </label>
            <label class="field-label">
              Pool mín
              <input
                type="number"
                class="field-input"
                v-model.number="cfg.poolMin"
                :disabled="isReadOnly"
                :readonly="isReadOnly"
                @input="markConfigDirty(cfg)"
              />
            </label>
            <label class="field-label">
              Pool máx
              <input
                type="number"
                class="field-input"
                v-model.number="cfg.poolMax"
                :disabled="isReadOnly"
                :readonly="isReadOnly"
                @input="markConfigDirty(cfg)"
              />
            </label>
            <label class="field-label">
              Acertos (k)
              <input
                type="number"
                class="field-input"
                v-model.number="cfg.hitsRequired"
                :disabled="isReadOnly"
                :readonly="isReadOnly"
                @input="markConfigDirty(cfg)"
              />
            </label>
            <label class="field-label">
              Aposta mín (R$)
              <input
                type="number"
                min="0"
                step="0.01"
                class="field-input"
                v-model.number="cfg.minBetValue"
                :disabled="isReadOnly"
                :readonly="isReadOnly"
                @input="markConfigDirty(cfg)"
              />
            </label>
            <label class="field-label">
              Aposta máx (R$)
              <input
                type="number"
                min="0"
                step="0.01"
                class="field-input"
                v-model.number="cfg.maxBetValue"
                :disabled="isReadOnly"
                :readonly="isReadOnly"
                @input="markConfigDirty(cfg)"
              />
            </label>
          </div>

          <table class="styled-table">
            <thead>
              <tr>
                <th>Qtd. dezenas (n)</th>
                <th>Cotação</th>
                <th>Min. acertos</th>
                <th v-if="!isReadOnly"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(cot, idx) in visibleCotacoes(cfg)" :key="cot.id || idx">
                <td>
                  <input
                    type="number"
                    class="field-input table-input"
                    v-model.number="cot.pickCount"
                    :disabled="isReadOnly"
                    :readonly="isReadOnly"
                    @input="markCotDirty(cfg, cot)"
                  />
                </td>
                <td>
                  <input
                    type="number"
                    step="0.01"
                    class="field-input table-input"
                    v-model.number="cot.cotacao"
                    :disabled="isReadOnly"
                    :readonly="isReadOnly"
                    @input="markCotDirty(cfg, cot)"
                  />
                </td>
                <td>
                  <input
                    type="number"
                    class="field-input table-input"
                    v-model.number="cot.minHits"
                    :disabled="isReadOnly"
                    :readonly="isReadOnly"
                    @input="markCotDirty(cfg, cot)"
                  />
                </td>
                <td v-if="!isReadOnly" class="actions-cell">
                  <button
                    type="button"
                    class="btn-remove"
                    title="Remover"
                    aria-label="Remover"
                    @click="removeCotacao(cfg, cot)"
                  >
                    <i class="fa-solid fa-trash"></i>
                  </button>
                </td>
              </tr>
              <tr v-if="visibleCotacoes(cfg).length === 0">
                <td :colspan="isReadOnly ? 3 : 4" class="empty-row">
                  Nenhuma cotação cadastrada
                </td>
              </tr>
            </tbody>
          </table>

          <div v-if="!isReadOnly" class="content-actions">
            <button type="button" class="btn-add" @click="addCotacao(cfg)">
              <i class="fa-solid fa-plus"></i>
              Adicionar cotação
            </button>
          </div>
        </div>
      </div>

      <div v-if="!isReadOnly" class="save-button-container">
        <button
          type="button"
          class="btn btn-save"
          :disabled="saving"
          @click="save"
        >
          <i class="fa-solid fa-save"></i>
          {{ saving ? 'Salvando...' : 'Salvar cotações de loteria' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import mainService from "../../services/mainService";
import { toast } from "@/composables/useToast";
import { handleErrorMessage } from "@/services/config";

export default {
  name: "RegionsConfigsLotteryCotacao",
  props: {
    canEdit: { type: Boolean, default: true },
    isReadOnly: { type: Boolean, default: false },
  },
  data() {
    return {
      loading: false,
      saving: false,
      regionId: null,
      configs: [],
      expanded: {},
    };
  },
  mounted() {
    this.load();
  },
  methods: {
    accordionKey(cfg) {
      return String(cfg.id || cfg.modalityId || cfg.modalityName);
    },
    isExpanded(cfg) {
      return !!this.expanded[this.accordionKey(cfg)];
    },
    toggleAccordion(cfg) {
      const key = this.accordionKey(cfg);
      this.expanded = { ...this.expanded, [key]: !this.expanded[key] };
    },
    visibleCotacoes(cfg) {
      return (cfg.cotacoes || []).filter(c => c.operation !== "D");
    },
    isTieredHitsPoolConfig(cfg) {
      const name = (cfg?.modalityName || "").toUpperCase();
      const type = Number(cfg?.modalityType);
      return (
        name.includes("BINGUINHO") ||
        name.includes("BINGO SEMANAL") ||
        name.includes("LOTO QUINA") ||
        type === 21 ||
        type === 22 ||
        type === 23
      );
    },
    isBinguinhoConfig(cfg) {
      return this.isTieredHitsPoolConfig(cfg);
    },
    markConfigDirty(cfg) {
      if (!cfg.operation || cfg.operation === "") {
        cfg.operation = cfg.id ? "U" : "I";
      }
    },
    markCotDirty(cfg, cot) {
      this.markConfigDirty(cfg);
      if (!cot.operation || cot.operation === "") {
        cot.operation = cot.id ? "U" : "I";
      }
    },
    addCotacao(cfg) {
      this.markConfigDirty(cfg);
      if (!cfg.cotacoes) cfg.cotacoes = [];
      cfg.cotacoes.push({
        id: null,
        pickCount: cfg.hitsRequired || 15,
        cotacao: 0,
        minHits: cfg.hitsRequired || 15,
        operation: "I",
      });
    },
    removeCotacao(cfg, cot) {
      this.markConfigDirty(cfg);
      if (cot.id) {
        cot.operation = "D";
      } else {
        cfg.cotacoes = cfg.cotacoes.filter(c => c !== cot);
      }
    },
    async load() {
      this.loading = true;
      try {
        const currentRegion = JSON.parse(localStorage.getItem("currentRegion") || "{}");
        this.regionId = currentRegion.id;
        const res = await mainService.searchRegionLotteryCotacao();
        const data = res?.data?.result || res?.data || res;
        this.configs = (data.configs || [])
          .map((c, index) => ({
            ...c,
            sortOrder: c.sortOrder > 0 ? c.sortOrder : index + 1,
            minBetValue: Number(c.minBetValue) || 0,
            maxBetValue: Number(c.maxBetValue) || 0,
            operation: "",
            cotacoes: (c.cotacoes || []).map(x => ({ ...x, operation: "" })),
          }))
          .sort((a, b) => a.sortOrder - b.sortOrder);

        if (this.configs.length > 0 && Object.keys(this.expanded).length === 0) {
          const firstKey = this.accordionKey(this.configs[0]);
          this.expanded = { [firstKey]: true };
        }
      } catch (e) {
        console.error(e);
        this.configs = [];
        toast.error(handleErrorMessage(e) || "Erro ao carregar cotações de loteria.");
      } finally {
        this.loading = false;
      }
    },
    async save() {
      if (this.isReadOnly || !this.regionId) return;
      this.saving = true;
      try {
        const payload = {
          regionId: this.regionId,
          configs: this.configs.map(c => ({
            id: c.id,
            modalityId: c.modalityId,
            poolMin: c.poolMin,
            poolMax: c.poolMax,
            hitsRequired: c.hitsRequired,
            isActive: c.isActive,
            sortOrder: c.sortOrder,
            minBetValue: Number(c.minBetValue) || 0,
            maxBetValue: Number(c.maxBetValue) || 0,
            operation: c.operation || "U",
            cotacoes: (c.cotacoes || []).map(x => ({
              id: x.id,
              pickCount: x.pickCount,
              cotacao: x.cotacao,
              minHits: x.minHits > 0 ? x.minHits : c.hitsRequired,
              operation: x.operation || (x.id ? "U" : "I"),
            })),
          })),
        };
        await mainService.saveRegionLotteryCotacao(payload);
        await this.load();
        this.$emit("saved");
        toast.success("Cotações de loteria salvas!", { duration: 3000 });
      } catch (e) {
        console.error(
          "Erro ao salvar cotações:",
          e?.response?.data || e
        );
        toast.error(handleErrorMessage(e) || "Erro ao salvar cotações de loteria.");
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.lottery-cotacao {
  width: 100%;
  padding: 8px 0;
}

.hint {
  color: #475569;
  font-size: 13px;
  margin-bottom: 16px;
  line-height: 1.45;
}

[data-theme="dark"] .hint {
  color: #a1a1aa;
}

.configs-loading {
  display: flex;
  gap: 8px;
  align-items: center;
  color: #64748b;
  padding: 12px 0;
}

[data-theme="dark"] .configs-loading {
  color: #a1a1aa;
}

.lotteries-accordion {
  width: 100%;
  max-width: 100%;
  overflow: visible;
}

.accordion-item {
  margin-bottom: 12px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  background-color: #ffffff;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
}

.accordion-item:hover {
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}

[data-theme="dark"] .accordion-item {
  background-color: rgba(37, 37, 41, 0.9);
  border-color: #52525b;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.3);
}

[data-theme="dark"] .accordion-item:hover {
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.4);
}

.accordion-header {
  display: flex;
  align-items: center;
  padding: 16px 20px;
  cursor: pointer;
  background-color: #f1f5f9;
  transition: all 0.3s ease;
  user-select: none;
  border-radius: 8px;
  gap: 8px;
}

.accordion-header:hover {
  background-color: #e2e8f0;
}

.accordion-header.active {
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  color: white;
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
}

[data-theme="dark"] .accordion-header {
  background-color: #1a1a1d;
}

[data-theme="dark"] .accordion-header:hover {
  background-color: #252529;
}

[data-theme="dark"] .accordion-header.active {
  background: linear-gradient(135deg, #60a5fa, #3b82f6);
}

.accordion-icon {
  font-size: 14px;
  margin-right: 4px;
  color: #64748b;
}

[data-theme="dark"] .accordion-icon {
  color: #a1a1aa;
}

.accordion-header.active .accordion-icon {
  color: white;
}

.lottery-name {
  font-weight: 600;
  font-size: 16px;
  flex: 1;
  color: #1e293b;
}

[data-theme="dark"] .lottery-name {
  color: #f4f4f5;
}

.accordion-header.active .lottery-name {
  color: white;
}

.lottery-count {
  font-size: 14px;
  opacity: 0.85;
  color: #475569;
}

[data-theme="dark"] .lottery-count {
  color: #e4e4e7;
}

.accordion-header.active .lottery-count {
  color: white;
}

.active-toggle {
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 6px;
  color: #1e293b;
  margin-left: 12px;
  cursor: pointer;
}

[data-theme="dark"] .active-toggle {
  color: #f4f4f5;
}

.accordion-header.active .active-toggle {
  color: white;
}

.accordion-content {
  padding: 16px;
  overflow-x: auto;
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
}

[data-theme="dark"] .accordion-content {
  background: #0e0e0f;
}

.config-row {
  display: flex;
  flex-wrap: nowrap;
  align-items: flex-end;
  gap: 10px;
  margin-bottom: 16px;
  width: 100%;
}

.field-label {
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1 1 0;
  min-width: 0;
  font-size: 12px;
  font-weight: 600;
  color: #475569;
}

[data-theme="dark"] .field-label {
  color: #a1a1aa;
}

.field-input {
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  padding: 8px 10px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  background: #ffffff;
  color: #1e293b;
  font-size: 13px;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.field-input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
}

.field-input:disabled,
.field-input:read-only {
  background: #f1f5f9;
  color: #64748b;
  cursor: not-allowed;
}

[data-theme="dark"] .field-input {
  background: #1a1a1d;
  border-color: #52525b;
  color: #f4f4f5;
}

[data-theme="dark"] .field-input:focus {
  border-color: #60a5fa;
  box-shadow: 0 0 0 3px rgba(96, 165, 250, 0.2);
}

[data-theme="dark"] .field-input:disabled,
[data-theme="dark"] .field-input:read-only {
  background: #252529;
  color: #a1a1aa;
}

.table-input {
  min-width: 90px;
  width: 100%;
  max-width: 140px;
}

.styled-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  margin-bottom: 12px;
}

.styled-table thead {
  background-color: #1a1a1d;
}

.styled-table th {
  padding: 10px 8px;
  text-align: left;
  font-weight: 600;
  border-bottom: 2px solid #52525b;
  font-size: 12px;
  white-space: nowrap;
  color: #ffffff;
}

[data-theme="dark"] .styled-table th {
  color: #f4f4f5;
}

.styled-table td {
  padding: 10px 8px;
  text-align: left;
  border-bottom: 1px solid #e2e8f0;
  vertical-align: middle;
  color: #1e293b;
}

[data-theme="dark"] .styled-table td {
  border-bottom-color: #52525b;
  color: #f4f4f5;
  background: #0e0e0f;
}

.styled-table tbody tr:hover {
  background-color: #f8fafc;
}

[data-theme="dark"] .styled-table tbody tr:hover {
  background-color: #252529;
}

.empty-row {
  text-align: center !important;
  color: #64748b !important;
  font-style: italic;
  padding: 16px !important;
}

[data-theme="dark"] .empty-row {
  color: #a1a1aa !important;
}

.actions-cell {
  width: 48px;
  text-align: center !important;
}

.btn-remove {
  background: transparent;
  color: #dc2626;
  border: none;
  border-radius: 4px;
  padding: 6px 8px;
  cursor: pointer;
  font-size: 14px;
  line-height: 1;
}

.btn-remove:hover {
  color: #991b1b;
}

[data-theme="dark"] .btn-remove {
  color: #f87171;
}

[data-theme="dark"] .btn-remove:hover {
  color: #fca5a5;
}

.content-actions {
  display: flex;
  justify-content: flex-start;
}

.btn-add {
  background: #3b82f6;
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 8px 14px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.btn-add:hover {
  background: #2563eb;
}

[data-theme="dark"] .btn-add {
  background: #60a5fa;
  color: #0e0e0f;
}

[data-theme="dark"] .btn-add:hover {
  background: #93c5fd;
}

.save-button-container {
  margin-top: 20px;
  padding-top: 16px;
  border-top: 2px solid #3b82f6;
  display: flex;
  justify-content: center;
}

[data-theme="dark"] .save-button-container {
  border-top-color: #60a5fa;
}

.btn-save {
  padding: 10px 30px;
  font-size: 16px;
  font-weight: bold;
  border-radius: 8px;
  border: none;
  background-color: #28a745;
  color: white;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.btn-save:hover:not(:disabled) {
  background-color: #218838;
  transform: translateY(-1px);
}

.btn-save:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}
</style>
